import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from 'api/api'
import { BsvWalletType, initWallet, IWalletInterface } from 'wallets/wallets'
import queryClient from 'api/queryClient'

export const WALLET_DETAILS_QUERY_KEY = 'wallet-details'

export interface WalletDetails {
    balance: number
    confirmedBalance?: number
    unconfirmedBalance?: number
    address: string
    ordinalsAddress: string
    wallet: IWalletInterface
    balanceLoaded: boolean
    addressLoaded: boolean
}

const FALLBACK_INITIAL_DELAY_MS = 3000
const FALLBACK_POLL_INTERVAL_MS = 2000
const FALLBACK_MAX_ATTEMPTS = 5

type FallbackPollerState = {
    subscribers: number
    running: boolean
    attempts: number
    timeoutId: ReturnType<typeof setTimeout> | null
    intervalId: ReturnType<typeof setInterval> | null
}

type AddressLoadResponse = {
    ok?: boolean
    address_loaded?: boolean
    address?: string
    ordinals_address?: string
}

const balanceFallbackPollers = new Map<BsvWalletType, FallbackPollerState>()
const addressFallbackPollers = new Map<BsvWalletType, FallbackPollerState>()

const hasAddressData = (details?: Pick<WalletDetails, 'address' | 'ordinalsAddress'>) =>
    Boolean(details?.address && details?.ordinalsAddress)

export const isBsvWalletType = (walletType?: BsvWalletType | null): walletType is BsvWalletType =>
    walletType === BsvWalletType.SHUALLET || walletType === BsvWalletType.PANDA

const seedWalletDetails = (walletType: BsvWalletType, oldData?: WalletDetails): WalletDetails => ({
    wallet: oldData?.wallet || initWallet(walletType),
    balance: oldData?.balance || 0,
    confirmedBalance: oldData?.confirmedBalance,
    unconfirmedBalance: oldData?.unconfirmedBalance,
    address: oldData?.address || '',
    ordinalsAddress: oldData?.ordinalsAddress || '',
    balanceLoaded: oldData?.balanceLoaded || false,
    addressLoaded: oldData?.addressLoaded || false,
})

const stopFallbackPoller = (state: FallbackPollerState) => {
    if (state.timeoutId) clearTimeout(state.timeoutId)
    if (state.intervalId) clearInterval(state.intervalId)
    state.timeoutId = null
    state.intervalId = null
    state.running = false
    state.attempts = 0
}

export default function useWalletDetails(walletType?: BsvWalletType | null) {
    const validWalletType = isBsvWalletType(walletType) ? walletType : null

    const query = useQuery<WalletDetails>({
        queryKey: [WALLET_DETAILS_QUERY_KEY, validWalletType],
        queryFn: async () => {
            if (!validWalletType) {
                throw new Error('Wallet type is required')
            }

            const wallet = initWallet(validWalletType)
            let initialAddressResponse: AddressLoadResponse | null = null

            // Trigger the balance worker immediately. For addresses, the endpoint is cache-first
            // and enqueues only when the cache misses.
            await Promise.all([
                wallet.getBalance(),
                validWalletType === BsvWalletType.SHUALLET
                    ? api
                          .getBsvAddress()
                          .then(response => {
                              initialAddressResponse = response
                          })
                          .catch(() => {})
                    : wallet.getAddress(),
            ])

            // Check if we already have loaded data in the cache
            const existingData = queryClient.getQueryData<WalletDetails>([
                WALLET_DETAILS_QUERY_KEY,
                validWalletType,
            ])

            if (initialAddressResponse?.ok && initialAddressResponse?.address_loaded) {
                const seedData = seedWalletDetails(validWalletType, existingData)
                const address = initialAddressResponse.address || seedData.address || ''
                const ordinalsAddress =
                    initialAddressResponse.ordinals_address || seedData.ordinalsAddress || ''

                return {
                    ...seedData,
                    wallet,
                    address,
                    ordinalsAddress,
                    addressLoaded: Boolean(address),
                }
            }

            if (existingData?.balanceLoaded) {
                return { ...existingData, wallet }
            }

            // return initial state - actual values will be updated via websocket
            return { ...seedWalletDetails(validWalletType, existingData), wallet }
        },
        staleTime: 30 * 1000, // refresh after 30 seconds
        enabled: !!validWalletType && !!gon?.currentUser, // only run if we have a wallet type and logged in
        gcTime: Infinity,
        retry: 0,
        refetchOnMount: true, // refetch on mount if data is stale
        refetchOnWindowFocus: true, // refetch on focus if data is stale
        refetchOnReconnect: true, // refetch if connection is re-established
    })

    useEffect(() => {
        // Only run for logged in users
        if (!gon?.currentUser) return
        if (!validWalletType) return

        // The main path is websocket-driven. This bounded fallback uses async enqueue + cache reads
        // so we never perform third-party balance lookups on the main request thread.
        if (validWalletType !== BsvWalletType.SHUALLET) return

        const poller = balanceFallbackPollers.get(validWalletType) || {
            subscribers: 0,
            running: false,
            attempts: 0,
            timeoutId: null,
            intervalId: null,
        }
        balanceFallbackPollers.set(validWalletType, poller)
        poller.subscribers += 1

        const pollCachedBalance = async () => {
            if (!poller.running) return

            const latest = queryClient.getQueryData<WalletDetails>([
                WALLET_DETAILS_QUERY_KEY,
                validWalletType,
            ])
            if (latest?.balanceLoaded) {
                stopFallbackPoller(poller)
                return
            }

            poller.attempts += 1
            try {
                const response = await api.getBsvBalanceCached()
                if (response?.ok && response?.balance_loaded) {
                    queryClient.setQueryData<WalletDetails>(
                        [WALLET_DETAILS_QUERY_KEY, validWalletType],
                        oldData => {
                            const seedData = oldData || {
                                wallet: initWallet(validWalletType),
                                balance: 0,
                                address: '',
                                ordinalsAddress: '',
                                balanceLoaded: false,
                                addressLoaded: false,
                            }

                            return {
                                ...seedData,
                                balance: Number(response.balance_sats || 0),
                                confirmedBalance: Number(response.confirmed_sats || 0),
                                unconfirmedBalance: Number(response.unconfirmed_sats || 0),
                                balanceLoaded: true,
                                // If we already have an address value, treat it as loaded so wallet UI doesn't
                                // remain stuck waiting on a separate address worker failure path.
                                addressLoaded: seedData.addressLoaded || Boolean(seedData.address),
                            }
                        },
                    )
                    stopFallbackPoller(poller)
                    return
                }
            } catch (_) {
                // Ignore transient fallback polling errors; websocket path remains primary.
            }

            if (poller.attempts >= FALLBACK_MAX_ATTEMPTS) {
                stopFallbackPoller(poller)
            }
        }

        if (!poller.running) {
            const currentData = queryClient.getQueryData<WalletDetails>([
                WALLET_DETAILS_QUERY_KEY,
                validWalletType,
            ])
            if (!currentData?.balanceLoaded) {
                poller.running = true
                poller.attempts = 0
                poller.timeoutId = setTimeout(() => {
                    // Kick the worker path and then poll cached results.
                    void api.getBsvBalance().catch(() => {})
                    void pollCachedBalance()
                    poller.intervalId = setInterval(() => {
                        void pollCachedBalance()
                    }, FALLBACK_POLL_INTERVAL_MS)
                }, FALLBACK_INITIAL_DELAY_MS)
            }
        }

        return () => {
            poller.subscribers = Math.max(0, poller.subscribers - 1)
            if (poller.subscribers === 0) {
                stopFallbackPoller(poller)
                balanceFallbackPollers.delete(validWalletType)
            }
        }
    }, [validWalletType])

    useEffect(() => {
        if (!gon?.currentUser) return
        if (!validWalletType) return
        if (validWalletType !== BsvWalletType.SHUALLET) return

        const poller = addressFallbackPollers.get(validWalletType) || {
            subscribers: 0,
            running: false,
            attempts: 0,
            timeoutId: null,
            intervalId: null,
        }
        addressFallbackPollers.set(validWalletType, poller)
        poller.subscribers += 1

        const pollAddress = async () => {
            if (!poller.running) return

            const latest = queryClient.getQueryData<WalletDetails>([
                WALLET_DETAILS_QUERY_KEY,
                validWalletType,
            ])
            if (hasAddressData(latest)) {
                stopFallbackPoller(poller)
                return
            }

            poller.attempts += 1
            try {
                const response = await api.getBsvAddress({ enqueue: false })
                if (response?.ok && response?.address_loaded) {
                    let loadedAddress = ''
                    let loadedOrdinalsAddress = ''

                    queryClient.setQueryData<WalletDetails>(
                        [WALLET_DETAILS_QUERY_KEY, walletType],
                        oldData => {
                            const seedData = seedWalletDetails(walletType, oldData)

                            loadedAddress = response.address || seedData.address || ''
                            loadedOrdinalsAddress =
                                response.ordinals_address || seedData.ordinalsAddress || ''

                            return {
                                ...seedData,
                                address: loadedAddress,
                                ordinalsAddress: loadedOrdinalsAddress,
                                addressLoaded: Boolean(loadedAddress),
                            }
                        },
                    )

                    if (loadedAddress && loadedOrdinalsAddress) {
                        stopFallbackPoller(poller)
                        return
                    }
                }
            } catch (_) {
                // Ignore transient fallback polling errors; websocket path remains primary.
            }

            if (poller.attempts >= FALLBACK_MAX_ATTEMPTS) {
                stopFallbackPoller(poller)
            }
        }

        if (!poller.running) {
            const currentData = queryClient.getQueryData<WalletDetails>([
                WALLET_DETAILS_QUERY_KEY,
                validWalletType,
            ])
            if (!hasAddressData(currentData)) {
                poller.running = true
                poller.attempts = 0

                poller.timeoutId = setTimeout(() => {
                    void pollAddress()
                    poller.intervalId = setInterval(() => {
                        void pollAddress()
                    }, FALLBACK_POLL_INTERVAL_MS)
                }, FALLBACK_INITIAL_DELAY_MS)
            }
        }

        return () => {
            poller.subscribers = Math.max(0, poller.subscribers - 1)
            if (poller.subscribers === 0) {
                stopFallbackPoller(poller)
                addressFallbackPollers.delete(validWalletType)
            }
        }
    }, [validWalletType])

    return query
}
